import { useState } from 'react';
import { usePendingMemberProposals } from '@/hooks/admin/member/use-pending-member-proposals';
import { usePendingInvites } from '@/hooks/admin/member/use-pending-invites';
import { useListMembers } from '@/hooks/organization/use-list-members';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { Plus, MoreHorizontal, Loader2, Mail } from 'lucide-react';
import {
  StyledTable,
  StyledTableWrapper,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHead,
  StyledTableCell,
} from '@/components/ui/styled-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  PageHeader,
  PageTitle,
  PageDescription,
  PageHeaderInfo,
  PageContent,
  PageAction,
} from '@/components/layout/page-header';
import { NewMemberDialog } from '@/components/members/new-member-dialog';
import { EditMemberNameDialog } from '@/components/members/edit-member-name-dialog';
import { MemberMenuOptions } from '@/components/members/member-menu-options';
import { PendingChangesAlert } from '@/components/shared/pending-changes-alert';
import { ReviewMemberChangesDialog } from '@/pages/members/components/review-member-changes-dialog';
import { InvitedMembersDialog } from '@/components/members/invited-members-dialog';
import { useMemberProposalSignature } from '@/hooks/admin/member/use-member-proposal-signature';
import { useRemoveMember } from '@/hooks/admin/member/use-remove-member';
import { formatName } from '@/lib/name';
import { getEmailDisplay, type OrganizationMember } from '@/types/user';
import {
  ReorderableList,
  SortableItem,
} from '@/components/shared/reorderable-list';
import { ReorderError } from '@/components/shared/reorder-error';
import { useOrganizationItemOrder } from '@/hooks/admin/organization/use-organization-item-order';

type Member = OrganizationMember;
const getMemberId = (member: Member) => member.id;

export function MembersPage() {
  const [isNewMemberDialogOpen, setIsNewMemberDialogOpen] = useState(false);
  const [isInvitedMembersDialogOpen, setIsInvitedMembersDialogOpen] =
    useState(false);
  const [confirmDeleteMember, setConfirmDeleteMember] = useState<Member | null>(
    null
  );
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | number | null>(
    null
  );
  const [isReviewChangesDialogOpen, setIsReviewChangesDialogOpen] =
    useState(false);
  const [selectedMemberForRename, setSelectedMemberForRename] =
    useState<Member | null>(null);
  const [isEditMemberNameDialogOpen, setIsEditMemberNameDialogOpen] =
    useState(false);

  const handleRemoveMemberClick = (member: Member) => {
    setConfirmDeleteMember(member);
  };

  const handleEditMemberName = (member: Member) => {
    setSelectedMemberForRename(member);
    setIsEditMemberNameDialogOpen(true);
  };

  const handleEditMemberNameDialogOpenChange = (open: boolean) => {
    setIsEditMemberNameDialogOpen(open);
    if (!open) {
      setSelectedMemberForRename(null);
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!confirmDeleteMember || !organization?.id) return;

    try {
      await removeMember({
        removingUserId: confirmDeleteMember.id,
      });
      setConfirmDeleteMember(null);
    } catch (error) {
      console.error('Failed to remove member:', error);
      // Error is already handled by the hook's onError callback
    }
  };

  const handleCancelRemoveMember = () => {
    setConfirmDeleteMember(null);
  };

  const handleReviewChanges = () => {
    setIsReviewChangesDialogOpen(true);
  };

  // NOTE: Using useMyOrganization() for orgId is a temporary approach.
  // This is subject to change; a React context will likely provide org state centrally.
  // In the future, users will have more than one organization, so we need to use the organizationId from the context.
  const { organization } = useMyOrganization();
  const isAdmin = organization?.isAdmin ?? false;
  const [isProposalStateAvailable, setIsProposalStateAvailable] =
    useState(true);
  const { proposals, refresh: refreshProposals } = usePendingMemberProposals({
    onSuccess: (data) => {
      // Case: defined proposal data from a successful refresh restores proposal availability.
      if (data !== undefined) {
        setIsProposalStateAvailable(true);
      }
    },
  });
  const { members, refresh: refreshMembers } = useListMembers(
    organization?.id ?? null
  );
  const {
    orderedItems: orderedMembers,
    orderedIds: orderedMemberIds,
    reorder: reorderMembers,
    isReordering,
    reorderError,
  } = useOrganizationItemOrder({
    items: members,
    itemType: 'members',
    getItemId: getMemberId,
    enabled: isAdmin,
  });
  const {
    invites,
    isLoading: isLoadingInvites,
    refresh: refreshInvites,
  } = usePendingInvites(isAdmin ? (organization?.id ?? null) : null);

  const { createSignature } = useMemberProposalSignature({
    onSuccess: () => {
      refreshProposals();
      refreshMembers();
    },
  });

  const { removeMember, isLoading: isRemovingMember } = useRemoveMember({
    onSuccess: () => {
      refreshProposals();
      refreshMembers();
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const pendingChangesCount = proposals.length;

  return (
    <div className="">
      <PageHeader>
        <PageHeaderInfo>
          <PageTitle>Members</PageTitle>
          <PageDescription>Manage your organization's members</PageDescription>
        </PageHeaderInfo>
        <PageAction>
          <div className="flex flex-col items-end gap-4">
            {isAdmin && pendingChangesCount > 0 ? (
              <PendingChangesAlert
                pendingChangesCount={pendingChangesCount}
                onReviewChanges={handleReviewChanges}
              />
            ) : null}
            {organization?.isAdmin && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsInvitedMembersDialogOpen(true)}
                >
                  <Mail className="h-4 w-4 mr-1" />
                  Invited Members
                  {invites.length > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-primary text-primary-foreground">
                      {invites.length}
                    </span>
                  )}
                </Button>
                <Button onClick={() => setIsNewMemberDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  New Member
                </Button>
              </div>
            )}
          </div>
        </PageAction>
      </PageHeader>

      <PageContent>
        <ReorderError message={reorderError} />
        <StyledTableWrapper>
          <ReorderableList
            ids={orderedMemberIds}
            disabled={!isAdmin || isReordering}
            onReorder={reorderMembers}
          >
            <StyledTable>
              <StyledTableHeader>
                <tr>
                  {isAdmin ? (
                    <StyledTableHead className="w-10 px-1 first:pl-1" />
                  ) : null}
                  <StyledTableHead className={isAdmin ? 'pl-0' : undefined}>
                    Name
                  </StyledTableHead>
                  <StyledTableHead>Email</StyledTableHead>
                  <StyledTableHead></StyledTableHead>
                </tr>
              </StyledTableHeader>
              <StyledTableBody>
                {orderedMembers.length === 0 ? ( // This check is sufficient because by default there is at least 1 member
                  <StyledTableRow>
                    <StyledTableCell colSpan={isAdmin ? 4 : 3}>
                      <div className="h-48 flex items-center justify-center text-slate-500">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />{' '}
                        Loading members...
                      </div>
                    </StyledTableCell>
                  </StyledTableRow>
                ) : (
                  <>
                    {orderedMembers.map((member) => (
                      <SortableItem
                        key={member.id}
                        id={member.id}
                        label={formatName(member.name)}
                        disabled={!isAdmin || isReordering}
                      >
                        {({ setNodeRef, style, dragHandle }) => {
                          const isDropdownOpen = openDropdownId === member.id;
                          const tableRow = (
                            <StyledTableRow
                              ref={setNodeRef}
                              style={style}
                              className="hover:bg-muted/50"
                            >
                              {isAdmin ? (
                                <StyledTableCell className="w-10 px-1 first:pl-1 py-2 text-center">
                                  {dragHandle}
                                </StyledTableCell>
                              ) : null}
                              <StyledTableCell
                                className={isAdmin ? 'pl-0' : undefined}
                              >
                                <span className="text-base font-semibold">
                                  {formatName(member.name)}
                                </span>
                              </StyledTableCell>
                              <StyledTableCell>
                                {getEmailDisplay(member.email)}
                              </StyledTableCell>
                              <StyledTableCell className="text-center">
                                {isAdmin && (
                                  <DropdownMenu
                                    open={isDropdownOpen}
                                    onOpenChange={(open) =>
                                      setOpenDropdownId(open ? member.id : null)
                                    }
                                  >
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 data-[state=open]:bg-muted"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <MoreHorizontal className="h-4 w-4" />
                                        <span className="sr-only">
                                          Open menu
                                        </span>
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align="end"
                                      className="w-48 z-[9999]"
                                    >
                                      <MemberMenuOptions
                                        member={member}
                                        onEditName={handleEditMemberName}
                                        onRemoveMember={handleRemoveMemberClick}
                                        onActionComplete={() =>
                                          setOpenDropdownId(null)
                                        }
                                        MenuItem={DropdownMenuItem}
                                        MenuSeparator={DropdownMenuSeparator}
                                      />
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </StyledTableCell>
                            </StyledTableRow>
                          );

                          return isAdmin ? (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                {tableRow}
                              </ContextMenuTrigger>
                              <ContextMenuContent className="w-48 z-[9999]">
                                <MemberMenuOptions
                                  member={member}
                                  onEditName={handleEditMemberName}
                                  onRemoveMember={handleRemoveMemberClick}
                                  onActionComplete={() => {}}
                                  MenuItem={ContextMenuItem}
                                  MenuSeparator={ContextMenuSeparator}
                                />
                              </ContextMenuContent>
                            </ContextMenu>
                          ) : (
                            tableRow
                          );
                        }}
                      </SortableItem>
                    ))}
                  </>
                )}
              </StyledTableBody>
            </StyledTable>
          </ReorderableList>
        </StyledTableWrapper>
      </PageContent>

      <NewMemberDialog
        open={isNewMemberDialogOpen}
        onOpenChange={setIsNewMemberDialogOpen}
        onInviteSuccess={refreshInvites}
      />

      <InvitedMembersDialog
        open={isInvitedMembersDialogOpen}
        onOpenChange={setIsInvitedMembersDialogOpen}
        invites={invites}
        isLoading={isLoadingInvites}
        onInvitesChanged={refreshInvites}
      />

      <EditMemberNameDialog
        open={isEditMemberNameDialogOpen}
        member={selectedMemberForRename}
        onOpenChange={handleEditMemberNameDialogOpenChange}
        onUpdated={() => {
          refreshMembers();
        }}
      />

      {/* Confirmation Dialog for Removing Member */}
      <Dialog
        open={!!confirmDeleteMember}
        onOpenChange={() => setConfirmDeleteMember(null)}
      >
        <DialogContent
          className="z-[9999]"
          dialogOverlayProps={{ className: 'z-[9999]' }}
        >
          <DialogHeader>
            <DialogTitle>Remove Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove "
              {formatName(confirmDeleteMember?.name ?? '')}" from your
              organization? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelRemoveMember}
              disabled={isRemovingMember}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRemoveMember}
              disabled={isRemovingMember}
              className="relative"
            >
              <span className={isRemovingMember ? 'invisible' : ''}>
                Remove Member
              </span>
              {isRemovingMember ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </span>
              ) : null}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              Member Proposal will be created after signup
            </DialogTitle>
            <DialogDescription>
              The new member proposal will be created after the invitee has
              signed up and connected their mobile device.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowApprovalDialog(false)}>Okay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isAdmin ? (
        <ReviewMemberChangesDialog
          open={isReviewChangesDialogOpen}
          onOpenChange={setIsReviewChangesDialogOpen}
          proposals={proposals}
          onSignSelected={async (ids, decision) => {
            try {
              setIsSubmitting(true);
              for (const id of ids) {
                await createSignature({ proposalId: id, decision });
              }
            } finally {
              setIsSubmitting(false);
            }
          }}
          onApplied={() => {
            refreshProposals();
            refreshMembers();
          }}
          isProposalStateAvailable={isProposalStateAvailable}
          onProposalStateUnavailable={() => setIsProposalStateAvailable(false)}
          refreshProposals={refreshProposals}
          isSubmitting={isSubmitting}
        />
      ) : null}
    </div>
  );
}
